import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Tractor,
  Plus,
  Clock,
  Euro,
  Bell,
  FileText,
  User,
  Building2,
  ChevronRight,
  Check,
  AlertTriangle,
  Calendar,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fonction pour appeler l'API ETA
const etaAPI = {
  getClients: () => fetch(`${API_URL}/api/eta/clients`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()),
  
  getSalaries: () => fetch(`${API_URL}/api/eta/salaries`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()),
  
  getTypeTravaux: () => fetch(`${API_URL}/api/eta/types-travaux`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()),
  
  getChantiers: (params = '') => fetch(`${API_URL}/api/eta/chantiers${params}`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()),
  
  createChantier: (data) => fetch(`${API_URL}/api/eta/chantiers`, {
    method: 'POST',
    headers: { 
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  }).then(r => r.json()),
  
  getAlertes: () => fetch(`${API_URL}/api/eta/alertes`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()),
  
  getStats: () => fetch(`${API_URL}/api/eta/stats`, {
    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
  }).then(r => r.json()),
  
  getCompilation: (clientId, mois, annee) => 
    fetch(`${API_URL}/api/eta/facturation/compilation/${clientId}?mois=${mois}&annee=${annee}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
  
  genererFacture: (clientId, mois, annee) =>
    fetch(`${API_URL}/api/eta/facturation/generer/${clientId}?mois=${mois}&annee=${annee}`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    }).then(r => r.json()),
};

// Gros bouton type de travail
const TravailButton = ({ travail, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`p-4 rounded-2xl border-2 transition-all ${
      selected 
        ? 'border-primary bg-primary/10 shadow-lg' 
        : 'border-muted hover:border-primary/50'
    }`}
  >
    <span className="text-lg font-semibold">{travail.label}</span>
    <p className="text-sm text-muted-foreground mt-1">
      {travail.unite_defaut === 'heure' ? 'À l\'heure' : 'À l\'hectare'}
    </p>
  </button>
);

// Sélecteur client avec gros boutons
const ClientSelector = ({ clients, selected, onSelect }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {clients.map((client) => (
      <button
        key={client.id}
        onClick={() => onSelect(client)}
        className={`p-4 rounded-2xl border-2 text-left transition-all ${
          selected?.id === client.id 
            ? 'border-primary bg-primary/10 shadow-lg' 
            : 'border-muted hover:border-primary/50'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-primary" />
          </div>
          <div>
            <p className="font-bold text-lg">{client.nom}</p>
            <p className="text-sm text-muted-foreground">{client.commune}</p>
          </div>
        </div>
        {selected?.id === client.id && (
          <div className="mt-2 flex items-center text-primary">
            <Check className="w-5 h-5 mr-1" />
            Sélectionné
          </div>
        )}
      </button>
    ))}
  </div>
);

// Sélecteur salarié avec gros boutons
const SalarieSelector = ({ salaries, selected, onSelect }) => (
  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
    {salaries.map((salarie) => (
      <button
        key={salarie.id}
        onClick={() => onSelect(salarie)}
        className={`p-4 rounded-2xl border-2 text-center transition-all ${
          selected?.id === salarie.id 
            ? 'border-primary bg-primary/10 shadow-lg' 
            : 'border-muted hover:border-primary/50'
        }`}
      >
        <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center mx-auto mb-2 text-white text-2xl font-bold">
          {salarie.prenom?.[0]}{salarie.nom?.[0]}
        </div>
        <p className="font-semibold">{salarie.prenom}</p>
        <p className="text-sm text-muted-foreground">{salarie.poste}</p>
      </button>
    ))}
  </div>
);

// Carte d'alerte
const AlerteCard = ({ alerte, onTraiter }) => (
  <Card className="border-l-4 border-l-amber-500">
    <CardContent className="p-4">
      <div className="flex items-start justify-between">
        <div>
          <Badge className="bg-amber-100 text-amber-800 mb-2">À facturer</Badge>
          <h4 className="font-semibold">{alerte.titre}</h4>
          <p className="text-sm text-muted-foreground">{alerte.message}</p>
        </div>
        {alerte.montant && (
          <div className="text-right">
            <p className="text-lg font-bold">{alerte.montant?.toFixed(2)}€</p>
            <p className="text-xs text-muted-foreground">HT</p>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Modal nouveau chantier
const NouveauChantierModal = ({ open, onClose, clients, salaries, typesTravaux, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    client: null,
    salarie: null,
    typeTravail: null,
    heures: '',
    date: new Date().toISOString().split('T')[0],
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!form.client || !form.salarie || !form.typeTravail || !form.heures) {
      toast.error('Remplissez tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      const result = await etaAPI.createChantier({
        client_id: form.client.id,
        salarie_id: form.salarie.id,
        type_travail: form.typeTravail.code,
        date: form.date,
        heures_travaillees: parseFloat(form.heures),
      });
      
      toast.success(`Chantier enregistré: ${result.montant_ht?.toFixed(2)}€ HT`);
      onSuccess?.();
      onClose();
      setStep(1);
      setForm({ client: null, salarie: null, typeTravail: null, heures: '', date: new Date().toISOString().split('T')[0] });
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const canNext = () => {
    if (step === 1) return !!form.client;
    if (step === 2) return !!form.salarie;
    if (step === 3) return !!form.typeTravail;
    return true;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">Nouveau Chantier</DialogTitle>
        </DialogHeader>
        
        {/* Indicateur d'étapes */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                step >= s ? 'bg-primary text-white' : 'bg-muted'
              }`}
            >
              {s}
            </div>
          ))}
        </div>
        
        {/* Étape 1: Client */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-center">Chez quel client ?</h3>
            <ClientSelector 
              clients={clients} 
              selected={form.client} 
              onSelect={(c) => setForm({...form, client: c})}
            />
          </div>
        )}
        
        {/* Étape 2: Salarié */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-center">Qui a fait le travail ?</h3>
            <SalarieSelector 
              salaries={salaries} 
              selected={form.salarie} 
              onSelect={(s) => setForm({...form, salarie: s})}
            />
          </div>
        )}
        
        {/* Étape 3: Type de travail */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-center">Quel travail ?</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {typesTravaux.map((t) => (
                <TravailButton
                  key={t.code}
                  travail={t}
                  selected={form.typeTravail?.code === t.code}
                  onClick={() => setForm({...form, typeTravail: t})}
                />
              ))}
            </div>
          </div>
        )}
        
        {/* Étape 4: Heures et date */}
        {step === 4 && (
          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-center">Combien de temps ?</h3>
            
            <div className="max-w-xs mx-auto space-y-4">
              <div>
                <Label className="text-lg">Heures travaillées</Label>
                <Input
                  type="number"
                  value={form.heures}
                  onChange={(e) => setForm({...form, heures: e.target.value})}
                  placeholder="Ex: 8"
                  className="text-3xl h-16 text-center font-bold"
                  step="0.5"
                  min="0"
                />
              </div>
              
              <div>
                <Label className="text-lg">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({...form, date: e.target.value})}
                  className="text-xl h-14"
                />
              </div>
            </div>
            
            {/* Récapitulatif */}
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2">Récapitulatif</h4>
                <div className="space-y-1 text-sm">
                  <p><strong>Client:</strong> {form.client?.nom}</p>
                  <p><strong>Salarié:</strong> {form.salarie?.prenom} {form.salarie?.nom}</p>
                  <p><strong>Travail:</strong> {form.typeTravail?.label}</p>
                  <p><strong>Tarif:</strong> {
                    form.client?.tarifs?.[form.typeTravail?.code]?.tarif || 
                    form.client?.tarif_horaire_defaut
                  }€/{form.typeTravail?.unite_defaut}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
          >
            {step > 1 ? 'Retour' : 'Annuler'}
          </Button>
          
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canNext()}
              className="gap-2"
            >
              Suivant
              <ChevronRight className="w-4 h-4" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading || !form.heures}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Enregistrement...' : 'Valider le chantier'}
              <Check className="w-4 h-4" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ETAPage = () => {
  const [clients, setClients] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [typesTravaux, setTypesTravaux] = useState([]);
  const [chantiers, setChantiers] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNewChantier, setShowNewChantier] = useState(false);

  const fetchData = async () => {
    try {
      const [clientsData, salariesData, travauxData, chantiersData, alertesData, statsData] = 
        await Promise.all([
          etaAPI.getClients(),
          etaAPI.getSalaries(),
          etaAPI.getTypeTravaux(),
          etaAPI.getChantiers('?limit=20'),
          etaAPI.getAlertes(),
          etaAPI.getStats(),
        ]);
      
      setClients(clientsData || []);
      setSalaries(salariesData || []);
      setTypesTravaux(travauxData || []);
      setChantiers(chantiersData || []);
      setAlertes(alertesData || []);
      setStats(statsData);
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <div className="space-y-6 p-4" data-testid="eta-page">
      {/* Header avec gros bouton */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Tractor className="w-7 h-7 text-white" />
            </div>
            ETA
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des chantiers et facturation
          </p>
        </div>
        
        <Button 
          size="lg" 
          className="h-14 px-8 text-lg gap-2"
          onClick={() => setShowNewChantier(true)}
          data-testid="new-chantier-btn"
        >
          <Plus className="w-6 h-6" />
          Nouveau Chantier
        </Button>
      </div>
      
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.heures_totales?.toFixed(0) || 0}h</p>
                  <p className="text-sm text-muted-foreground">Ce mois</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.ca_facture?.toFixed(0) || 0}€</p>
                  <p className="text-sm text-muted-foreground">Facturé</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-amber-50 dark:bg-amber-950/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.ca_a_facturer?.toFixed(0) || 0}€</p>
                  <p className="text-sm text-muted-foreground">À facturer</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Bell className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.nb_alertes || 0}</p>
                  <p className="text-sm text-muted-foreground">Alertes</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      <Tabs defaultValue="alertes" className="space-y-4">
        <TabsList className="h-12">
          <TabsTrigger value="alertes" className="h-10 px-6">
            <Bell className="w-4 h-4 mr-2" />
            Alertes ({alertes.length})
          </TabsTrigger>
          <TabsTrigger value="chantiers" className="h-10 px-6">
            <Tractor className="w-4 h-4 mr-2" />
            Chantiers
          </TabsTrigger>
          <TabsTrigger value="clients" className="h-10 px-6">
            <Building2 className="w-4 h-4 mr-2" />
            Clients
          </TabsTrigger>
        </TabsList>
        
        {/* Alertes */}
        <TabsContent value="alertes" className="space-y-4">
          {alertes.length === 0 ? (
            <Card className="p-8 text-center">
              <Check className="w-12 h-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-xl font-semibold">Tout est à jour !</h3>
              <p className="text-muted-foreground">Aucune alerte en attente</p>
            </Card>
          ) : (
            <div className="space-y-3">
              {alertes.map((alerte) => (
                <AlerteCard key={alerte.id} alerte={alerte} />
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Chantiers récents */}
        <TabsContent value="chantiers" className="space-y-4">
          {chantiers.length === 0 ? (
            <Card className="p-8 text-center">
              <Tractor className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold">Aucun chantier</h3>
              <p className="text-muted-foreground mb-4">Commencez par créer un chantier</p>
              <Button onClick={() => setShowNewChantier(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Chantier
              </Button>
            </Card>
          ) : (
            <div className="space-y-3">
              {chantiers.map((chantier) => (
                <Card key={chantier.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Badge variant="outline">{chantier.date}</Badge>
                          <Badge className={
                            chantier.statut === 'facture' ? 'bg-green-100 text-green-800' :
                            chantier.statut === 'termine' ? 'bg-amber-100 text-amber-800' :
                            'bg-blue-100 text-blue-800'
                          }>
                            {chantier.statut}
                          </Badge>
                        </div>
                        <h4 className="font-semibold">{chantier.client_nom}</h4>
                        <p className="text-sm text-muted-foreground">
                          {chantier.type_travail_label} • {chantier.heures_travaillees}h • {chantier.salarie_nom}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold">{chantier.montant_ht?.toFixed(2)}€</p>
                        <p className="text-xs text-muted-foreground">HT</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
        
        {/* Clients */}
        <TabsContent value="clients" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {clients.map((client) => (
              <Card key={client.id} className="hover:shadow-lg transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-bold text-lg">{client.nom}</h4>
                      <p className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {client.commune}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">{client.tarif_horaire_defaut}€/h</p>
                      <p className="text-xs text-muted-foreground">Tarif défaut</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Modal nouveau chantier */}
      <NouveauChantierModal
        open={showNewChantier}
        onClose={() => setShowNewChantier(false)}
        clients={clients}
        salaries={salaries}
        typesTravaux={typesTravaux}
        onSuccess={fetchData}
      />
    </div>
  );
};

export default ETAPage;
